import { api } from './client';
import type { PAPromise } from './money';

/**
 * ============================================================================
 * THE MONEY-WRITE CLIENT — the wave the whole Personal Area exists for
 * ============================================================================
 *
 * W2 let a suspended subscriber READ the exact contract-wide figure that restores his
 * service. These are the routes that take the money.
 *
 *   GET    /pa/config                    — the operator's own text, UNAUTHENTICATED
 *   GET    /pa/payments/options          — what the form may offer, and the prefill
 *   POST   /pa/payments/card             — start a card top-up on the HOSTED form
 *   POST   /pa/payments/sbp              — issue an SBP QR (a one-off top-up)
 *   GET    /pa/payments/orders/{id}      — «ожидание подтверждения банка» → paid/failed
 *   DELETE /pa/payments/saved-card       — unlink, invalidating the anchor
 *   POST   /pa/payments/auto-payment     — the PROACTIVE monthly charge, on or off
 *   GET|POST /pa/payments/promise        — the terms, and taking one
 *   GET    /pa/payments/instructions     — «Другие способы оплаты», as Markdown
 *
 * It mirrors the wire structs in `backend/internal/personalarea/{payments_handlers,config}.go`,
 * pinned BOTH WAYS to `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ⚠ `GET /pa/payments/saved-card` IS DELIBERATELY NOT CALLED, and that is a decision
 * rather than an oversight. `savedCardOf` is ONE projection on the server, shared by
 * that route and by `options.saved_card` — so reading it separately would be a second
 * request describing the same card at a different instant, and a screen showing an
 * «Активна» card beside a form built for an expired one is precisely the class of seam
 * this project keeps paying for. The options read is one read for one instant; the
 * standalone route exists for a caller that wants the card alone, and this screen never
 * does.
 *
 * ⚠ NO CARD FIELD EXISTS IN THIS SPA. A card number is typed on the acquirer's own
 * hosted page, reached through `redirect_url`, and nothing here accepts, stores, logs or
 * echoes a pan, a token or an order identifier beyond what a subscriber must quote to
 * support.
 *
 * ⚠ MONEY IS AN INTEGER OF MINOR UNITS ON EVERY FIELD, and this module converts none of
 * it. `src/format.ts` holds the two arithmetic-free conversions (`formatMinor` out,
 * `parseMinor` back in) and `src/money-arithmetic.test.ts` fails if any module in this
 * app performs arithmetic on a money-bearing field.
 */

/**
 * `PAConfig` — the operator's own portal text, published to an UNAUTHENTICATED caller.
 *
 * Every field may be EMPTY and an empty field renders NOTHING: an unconfigured
 * installation shows a clean page rather than somebody else's operator name. The portal
 * invents no fallback for any of them.
 */
export interface PAConfig {
  product_title: string;
  login_welcome_text: string;
  support_phone: string;
  support_email: string;
  company_info: string;
  copyright_text: string;
  /**
   * ⚠ Rendered as the operator wrote it. A link to a THIRD-PARTY domain will not open
   * for a subscriber suspended for non-payment — the walled garden learns only the
   * acquirer's own domains — which is the registry entry's own warning and not something
   * this screen can fix.
   */
  terms_url: string;
}

/** `PASavedCard` — the linked recurring method (§2.6, §10a), masked. */
export interface PASavedCard {
  /** «12 asterisks + last 4», exactly as the acquirer callback supplied it. */
  masked_pan: string;
  card_holder: string;
  /** The payment system holding the token ("payonline"). */
  acquirer: string;
  /** "active" | "expired" — S17's closed pair. A method he UNLINKED is not reported. */
  status: string;
  /** The decline-classifier slug behind an "expired" status; "" when active. */
  expired_reason: string;
  /** The system could actually charge it today. An active-but-unchargeable method is an
   *  installation fault, and the screen must not promise auto-payments it cannot make. */
  chargeable: boolean;
  /** The PROACTIVE monthly auto-payment switch (§10) and NOTHING else. */
  auto_pay_enabled: boolean;
  saved_at: string;
}

/** `PAPaymentOptions` — everything the form needs before the subscriber types anything. */
export interface PAPaymentOptions {
  contract_id: number;
  currency: string;
  balance: number;
  /** Appendix L §3's individuals-only rule, ENFORCED: false means both POSTs refuse too. */
  payments_allowed: boolean;
  /** "" | "legal_entity" | "acquirer_unavailable" — two different sentences. */
  payments_blocked_reason: string;
  card_enabled: boolean;
  sbp_enabled: boolean;
  /** The instance's minimum top-up; 0 = none. Published so the form can refuse with the
   *  same figure the server refuses with, instead of discovering it as a 409. */
  min_amount: number;
  recommended_amount: number;
  recommended_mode: number;
  /** The CONTRACT-WIDE figure that lifts every suspension; 0 when nothing is suspended. */
  resume_amount: number;
  /**
   * ⚠ WHAT THE FORM PRE-FILLS, AND THE ONLY FIGURE IT MAY PRE-FILL. The selection rule —
   * «for a suspended Account the recommended (and pre-filled) amount is the EXACT amount
   * required to resume» — lives on the SERVER so that no channel can decide it
   * differently. This screen prints it and never chooses between the two beside it.
   */
  prefill_amount: number;
  /** `true` — «Запомнить карту» is CHECKED BY DEFAULT (`answers.md` P7-P1). */
  save_card_default: boolean;
  /** FALSE ⇒ the checkbox must not be rendered at all; the POST refuses it too. */
  save_card_available: boolean;
  /**
   * ⚠ THE FULL RUSSIAN CAPTION OF THE PRE-TICKED CHECKBOX, BUILT BY THE SERVER, AND THE
   * PORTAL RENDERS IT VERBATIM. P7-P1 makes it a requirement with three named parts —
   * what may be charged and when, that it earns the recurring-method discount, that it
   * can be removed at any time and where — precisely because a pre-ticked box is a
   * consent given by NOT acting, so the text nobody clicks on is the only text he is
   * guaranteed to have seen. It must not be shortened, split, paraphrased, or moved into
   * a tooltip; `PaymentScreen.test.tsx` asserts the rendered label equals this string.
   */
  save_card_caption: string;
  recurring_discount_percent: number;
  /** Where the 54-FZ receipt will go. ⚠ A DISPLAY AND NOT AN INPUT — nothing on this
   *  surface accepts a caller-supplied receipt address, so an editable box would be a
   *  field whose value nothing reads. */
  receipt_email: string;
  receipt_phone: string;
  saved_card: PASavedCard | null;
}

/** `PATopUpRequest` — the body of both top-up routes. */
export interface PATopUpRequest {
  /** MINOR UNITS, as everywhere else on this surface. */
  amount: number;
  /**
   * «Запомнить карту». Meaningful for the CARD instrument only — `POST /sbp` REFUSES a
   * request that sets it rather than dropping it silently, so the SBP call sends `false`
   * explicitly and never omits the field.
   */
  save_card: boolean;
}

/** `PATopUp` — a started card top-up, and where to send the payer. */
export interface PATopUp {
  provider: string;
  /** The acquirer ORDER identifier — what the status route is asked with, and what a
   *  subscriber quotes to support. Not a payment id in this system's ledger sense. */
  payment_id: string;
  /** The absolute GET redirect to the acquirer's HOSTED form. */
  redirect_url: string;
  amount: number;
}

/** `PAQRCode` — an issued SBP QR (§8d, D61). A one-off: it saves no method. */
export interface PAQRCode {
  provider: string;
  payment_id: string;
  /** The NSPK payment link a bank app opens. */
  payload: string;
  qr_code_id: string;
  /** The gateway's own rendering, base64. RETURNED and never stored — so a portal that
   *  shows it must not cache it either, which is why it lives in component state. */
  image_base64: string;
  amount: number;
  expires_at: string;
}

/** `PAOrder` — the state of a top-up this subscriber started (§8d step 3). */
export interface PAOrder {
  payment_id: string;
  /** "pending" | "paid" | "failed" | "expired" — the engine's own closed set. */
  state: string;
  /** "card" | "sbp". */
  instrument: string;
  amount: number;
  currency: string;
  created_at: string;
  expires_at: string;
  settled_at: string | null;
  /** The acquirer's own, so the screen can say WHICH of §8b's three failures happened. */
  failure_code: string;
  failure_reason: string;
}

/** `PAAutoPaymentRequest`. */
export interface PAAutoPaymentRequest {
  enabled: boolean;
}

/** `PAAutoPayment` — the switch's new state, echoed. */
export interface PAAutoPayment {
  contract_id: number;
  enabled: boolean;
}

/** `PAPromiseTerms` — §2.7/§21 as they apply to THIS contract and the CLIENT audience. */
export interface PAPromiseTerms {
  contract_id: number;
  /** Permitted TO THE SUBSCRIBER: the master flag AND «allow by client» (D120(c)). */
  allowed: boolean;
  min_amount: number;
  /** 0 means «no upper bound» and renders as «не ограничен» — never as «0,00», which
   *  would assert a bound that does not exist. */
  max_amount: number;
  term_days: number;
  /** The plain BALANCE and not availability: a subscriber in debt shown his availability
   *  would read as in credit. */
  balance: number;
  recommended_amount: number;
  /** READ-ONLY: `POST` decodes `{amount}` alone, so an editable picker would be a field
   *  whose value is dropped — the D109 class. */
  default_repay_by: string;
  can_grant: boolean;
  /** Literally the refusal the grant would produce, from the same function. */
  blocked_code: string;
  blocked_details: Record<string, unknown>;
  active_promise: PAPromise | null;
}

/** `PAPromiseRequest`. */
export interface PAPromiseRequest {
  amount: number;
}

/** `PAInstruction` — one §2.5a block. `body` is MARKDOWN, rendered sanitized. */
export interface PAInstruction {
  code: string;
  title: string;
  /**
   * ⚠ MARKDOWN, AUTHORED BY STAFF, RENDERED IN A SUBSCRIBER'S BROWSER. It is rendered by
   * `src/money/Markdown.tsx`, which builds React ELEMENTS and never an HTML string, so
   * raw markup in this field becomes visible text rather than nodes — and which has no
   * image node at all, so no body can reference an external asset the walled garden
   * cannot fetch.
   */
  body: string;
  sort_order: number;
}

/** `PAInstructions` — §8e's whole reading page. */
export interface PAInstructions {
  contract_id: number;
  /**
   * ⚠ IT MAY BE EMPTY AND THE PAGE IS STILL RENDERED. `contract.payment_code` is
   * `NOT NULL DEFAULT ''` with no generator, so on a real imported estate it is blank
   * while every fixture had one — the W2 review's finding E8, where the suspension notice
   * named a figure and offered no way to pay it.
   */
  payment_code: string;
  items: PAInstruction[];
  total: number;
}

// ---- calls ------------------------------------------------------------------

/**
 * The PUBLIC configuration read. It carries no session and must never be given one: the
 * LOGIN SCREEN renders it, and nobody is logged in there.
 */
export function getConfig(): Promise<PAConfig> {
  return api.get<PAConfig>('/pa/config');
}

export function getPaymentOptions(contractID: number): Promise<PAPaymentOptions> {
  return api.get<PAPaymentOptions>(`/pa/payments/options?contract_id=${contractID}`);
}

/**
 * startCardTopUp begins a card payment on the acquirer's hosted form.
 *
 * A double submit creates two INDEPENDENT orders, and the server decided that
 * deliberately: each credits through its own callback matched on its own order id, so no
 * double credit is possible, and handing back the first order's signed redirect for the
 * second tap would hand back a form that may already be stale. The SCREEN's duty is
 * therefore not idempotency — it is to disable the button while the request is in
 * flight, so a subscriber on a phone does not start two forms he must then reconcile.
 */
export function startCardTopUp(contractID: number, amountMinor: number, saveCard: boolean) {
  const body: PATopUpRequest = { amount: amountMinor, save_card: saveCard };
  return api.post<PATopUp>(`/pa/payments/card?contract_id=${contractID}`, body);
}

/**
 * startSBP issues a QR / payment link.
 *
 * `save_card` is sent as `false` EXPLICITLY rather than omitted: the field is required by
 * the schema, an SBP payment saves no method, and the server refuses a request that sets
 * it — so being explicit is what makes the client's intent match the refusal's shape.
 */
export function startSBP(contractID: number, amountMinor: number) {
  const body: PATopUpRequest = { amount: amountMinor, save_card: false };
  return api.post<PAQRCode>(`/pa/payments/sbp?contract_id=${contractID}`, body);
}

/**
 * getOrder polls one order the caller started.
 *
 * The id is URL-encoded because it is an acquirer-supplied string rather than a number of
 * ours, and it is the ONLY path parameter this SPA builds.
 */
export function getOrder(orderID: string): Promise<PAOrder> {
  return api.get<PAOrder>(`/pa/payments/orders/${encodeURIComponent(orderID)}`);
}

/** unlinkSavedCard is idempotent and answers 200 twice — see the handler's own note. */
export function unlinkSavedCard(contractID: number): Promise<{ status: string }> {
  return api.del<{ status: string }>(`/pa/payments/saved-card?contract_id=${contractID}`);
}

export function setAutoPayment(contractID: number, enabled: boolean): Promise<PAAutoPayment> {
  const body: PAAutoPaymentRequest = { enabled };
  return api.post<PAAutoPayment>(`/pa/payments/auto-payment?contract_id=${contractID}`, body);
}

export function getPromiseTerms(contractID: number): Promise<PAPromiseTerms> {
  return api.get<PAPromiseTerms>(`/pa/payments/promise?contract_id=${contractID}`);
}

export function takePromise(contractID: number, amountMinor: number): Promise<PAPromise> {
  const body: PAPromiseRequest = { amount: amountMinor };
  return api.post<PAPromise>(`/pa/payments/promise?contract_id=${contractID}`, body);
}

export function getInstructions(contractID: number): Promise<PAInstructions> {
  return api.get<PAInstructions>(`/pa/payments/instructions?contract_id=${contractID}`);
}
